const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem("token");
};

// API request helper
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const url = `${API_BASE_URL}/api/contractors${endpoint}`;

  const isFormData = options.body instanceof FormData;

  const config = {
    method: options.method || "GET",
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      // Don't set Content-Type for FormData - browser will set it with boundary
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    },
    ...options,
  };

  // Remove Content-Type from headers if FormData (browser will set it automatically)
  if (isFormData && config.headers["Content-Type"]) {
    delete config.headers["Content-Type"];
  }

  try {
    const response = await fetch(url, config);
    
    // Check if response is JSON before parsing
    const contentType = response.headers.get("content-type");
    let data;
    
    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      // If not JSON, try to get text for error messages
      const text = await response.text();
      console.error("Non-JSON response:", text);
      throw new Error(text || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!response.ok) {
      // Return error response with data so frontend can access errors object
      return {
        success: false,
        message: data.message || `HTTP error! status: ${response.status}`,
        errors: data.errors || {},
        ...data
      };
    }

    return data;
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

// Get all contractors
export const getContractors = async () => {
  try {
    const response = await apiRequest("");
    return response;
  } catch (error) {
    console.error("Failed to fetch contractors:", error);
    // Return mock data as fallback
    return {
      success: true,
      data: getMockContractors(),
      total: 15,
      message: "Found 15 contractors (mock data)",
    };
  }
};

// Get single contractor by ID
export const getContractor = async (id) => {
  try {
    const response = await apiRequest(`/${id}`);
    return response;
  } catch (error) {
    console.error("Failed to fetch contractor:", error);
    // Return mock data as fallback
    const mockContractor = getMockContractors().find((c) => c.id == id);
    if (!mockContractor) {
      throw new Error("Contractor not found");
    }
    return {
      success: true,
      data: mockContractor,
    };
  }
};

// Create new contractor
export const createContractor = async (contractorData) => {
  try {
    const config = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(contractorData),
    };

    const response = await apiRequest("", config);
    return response;
  } catch (error) {
    console.error("Failed to create contractor:", error);
    throw error;
  }
};

// Update contractor
export const updateContractor = async (id, contractorData) => {
  try {
    // Check if contractorData is FormData (for file uploads)
    const isFormData = contractorData instanceof FormData;

    const config = {
      method: "PUT",
    };

    if (isFormData) {
      // For FormData, don't set Content-Type header (browser will set it with boundary)
      config.body = contractorData;
    } else {
      // For JSON data
      config.headers = {
        "Content-Type": "application/json",
      };
      config.body = JSON.stringify(contractorData);
    }

    const response = await apiRequest(`/${id}`, config);
    return response;
  } catch (error) {
    console.error("Failed to update contractor:", error);
    throw error;
  }
};

// Delete contractor
export const deleteContractor = async (id) => {
  try {
    const response = await apiRequest(`/${id}`, {
      method: "DELETE",
    });
    return response;
  } catch (error) {
    console.error("Failed to delete contractor:", error);
    throw error;
  }
};

// Get contractor contacts
export const getContractorContacts = async () => {
  try {
    const response = await apiRequest("/contacts");
    return response;
  } catch (error) {
    console.error("Failed to fetch contractor contacts:", error);
    // Return mock data as fallback
    return {
      success: true,
      data: {
        contacts: getMockContractorContacts(),
        contactsForCalendar: getMockContactsForCalendar(),
      },
    };
  }
};

// Get contractor services
export const getContractorServices = async (contractorId) => {
  try {
    const response = await apiRequest(`/${contractorId}/services`);
    return response;
  } catch (error) {
    console.error("Failed to fetch contractor services:", error);
    // Return mock data as fallback
    return {
      success: true,
      data: getMockContractorServices(),
    };
  }
};

// Get all services
export const getServices = async () => {
  try {
    const response = await apiRequest("/services");
    return response;
  } catch (error) {
    console.error("Failed to fetch services:", error);
    // Return mock data as fallback
    return {
      success: true,
      data: getMockServices(),
    };
  }
};

// Create contractor contact
export const createContractorContact = async (contractorId, contactData) => {
  try {
    const response = await apiRequest(`/${contractorId}/contacts`, {
      method: "POST",
      body: JSON.stringify(contactData),
    });
    return response;
  } catch (error) {
    console.error("Failed to create contractor contact:", error);
    throw error;
  }
};

// Delete contractor contact
export const deleteContractorContact = async (contactId) => {
  try {
    const response = await apiRequest(`/contacts/${contactId}/destroy`, {
      method: "DELETE",
    });
    return response;
  } catch (error) {
    console.error("Failed to delete contractor contact:", error);
    throw error;
  }
};

// Manage contractor services
export const manageContractorServices = async (contractorId, serviceData) => {
  try {
    const response = await apiRequest(`/${contractorId}/services`, {
      method: "POST",
      body: JSON.stringify(serviceData),
    });
    return response;
  } catch (error) {
    console.error("Failed to manage contractor services:", error);
    throw error;
  }
};

// Remove contractor image
export const removeContractorImage = async (id, field) => {
  try {
    const response = await apiRequest(`/${id}/${field}`);
    return response;
  } catch (error) {
    console.error("Failed to remove contractor image:", error);
    throw error;
  }
};

// Mock data functions
const getMockContractors = () => {
  return [
    {
      id: 1,
      contractor_id: "CON001",
      name: "Accounts Team",
      logo: null,
      date_review: null,
      address_1: "123 Business Street",
      address_2: "Suite 100",
      address_3: "",
      town: "London",
      postcode: "SW1A 1AA",
      email: "michael.ashton@global-guardians.co.uk",
      email_alternative: "",
      phone_number: "",
      phone_number_alternative: "",
      fax_number: "",
      web: "",
      notes: "Internal accounts team",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 2,
      contractor_id: "CON002",
      name: "A&S Services 4 U Ltd",
      logo: null,
      date_review: "2020-09-18T00:00:00.000Z",
      address_1: "456 Service Road",
      address_2: "Unit 5",
      address_3: "",
      town: "Manchester",
      postcode: "M1 1AA",
      email: "karl@aservices4u.co.uk",
      email_alternative: "",
      phone_number: "0203 603 1369",
      phone_number_alternative: "",
      fax_number: "",
      web: "https://aservices4u.co.uk/",
      notes: "General maintenance services",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 3,
      contractor_id: "CON003",
      name: "AMES Group Ltd",
      logo: null,
      date_review: "2020-09-18T00:00:00.000Z",
      address_1: "789 Industrial Estate",
      address_2: "Building A",
      address_3: "",
      town: "Birmingham",
      postcode: "B1 1AA",
      email: "terry@amesgroup.co.uk",
      email_alternative: "",
      phone_number: "0121 443 100",
      phone_number_alternative: "",
      fax_number: "",
      web: "",
      notes: "Specialized construction services",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 4,
      contractor_id: "CON004",
      name: "Asbestos360",
      logo: null,
      date_review: "2023-01-31T00:00:00.000Z",
      address_1: "321 Safety Street",
      address_2: "",
      address_3: "",
      town: "Leeds",
      postcode: "LS1 1AA",
      email: "info@asbestos360.co.uk",
      email_alternative: "",
      phone_number: "014-42 238300",
      phone_number_alternative: "",
      fax_number: "",
      web: "www.asbestos360.co.uk",
      notes: "Asbestos removal and safety services",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 5,
      contractor_id: "CON005",
      name: "BAM Construction",
      logo: null,
      date_review: "2020-09-18T00:00:00.000Z",
      address_1: "654 Construction Way",
      address_2: "Site Office",
      address_3: "",
      town: "Liverpool",
      postcode: "L1 1AA",
      email: "",
      email_alternative: "",
      phone_number: "014-42 238300",
      phone_number_alternative: "",
      fax_number: "",
      web: "http://www.bam.co.uk",
      notes: "Major construction projects",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 6,
      contractor_id: "CON006",
      name: "Bap Glass",
      logo: null,
      date_review: "2023-02-28T00:00:00.000Z",
      address_1: "987 Glass Street",
      address_2: "",
      address_3: "",
      town: "Bristol",
      postcode: "BS1 1AA",
      email: "",
      email_alternative: "",
      phone_number: "02083711586",
      phone_number_alternative: "",
      fax_number: "",
      web: "www.bapglass.co.uk",
      notes: "Glass installation and repair",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 7,
      contractor_id: "CON007",
      name: "Biffa",
      logo: null,
      date_review: "2020-09-18T00:00:00.000Z",
      address_1: "147 Waste Management",
      address_2: "Recycling Center",
      address_3: "",
      town: "Sheffield",
      postcode: "S1 1AA",
      email: "david.tombin@biffa.co.uk",
      email_alternative: "",
      phone_number: "0800 307 307",
      phone_number_alternative: "",
      fax_number: "",
      web: "www.biffa.co.uk",
      notes: "Waste management and recycling services",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 8,
      contractor_id: "CON008",
      name: "Black&White Fire Safety",
      logo: null,
      date_review: "2020-09-18T00:00:00.000Z",
      address_1: "258 Fire Safety Road",
      address_2: "",
      address_3: "",
      town: "Newcastle",
      postcode: "NE1 1AA",
      email: "admin@bawk.com",
      email_alternative: "",
      phone_number: "01-28735777",
      phone_number_alternative: "",
      fax_number: "",
      web: "",
      notes: "Fire safety equipment and services",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 9,
      contractor_id: "CON009",
      name: "CC Landscape",
      logo: null,
      date_review: null,
      address_1: "369 Garden Street",
      address_2: "",
      address_3: "",
      town: "Cardiff",
      postcode: "CF1 1AA",
      email: "connorhall007@icloud.com",
      email_alternative: "",
      phone_number: "075729-40073",
      phone_number_alternative: "",
      fax_number: "",
      web: "",
      notes: "Landscaping and garden maintenance",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
      documents: [
        {
          id: 1,
          name: "Insurance Certificate",
          date_uploaded: "2024-01-10T10:00:00.000Z",
          reminder_sent: 0,
        },
        {
          id: 2,
          name: "Health and Safety Policy",
          date_uploaded: "2024-01-08T10:00:00.000Z",
          reminder_sent: 1,
        },
      ],
      contacts: [
        {
          id: 1,
          department: "Operations",
          reports_to: "Operations Manager",
          user: {
            id: 101,
            first_name: "Connor",
            last_name: "Hall",
            email: "connor.hall@cclandscape.co.uk",
            phone_number: "075729-40073",
            mobile_number: "075729-40073",
            fax_number: "",
            job_title: "Operations Director",
          },
        },
        {
          id: 2,
          department: "Sales",
          reports_to: "Connor Hall",
          user: {
            id: 102,
            first_name: "Sarah",
            last_name: "Wilson",
            email: "sarah.wilson@cclandscape.co.uk",
            phone_number: "02920-123456",
            mobile_number: "07700-123456",
            fax_number: "",
            job_title: "Sales Manager",
          },
        },
      ],
    },
    {
      id: 10,
      contractor_id: "CON010",
      name: "CEBES (Corporate Environmental Built Engineering Services)",
      logo: null,
      date_review: null,
      address_1: "741 Engineering Way",
      address_2: "Technical Center",
      address_3: "",
      town: "Edinburgh",
      postcode: "EH1 1AA",
      email: "catbee@btconnect.com",
      email_alternative: "",
      phone_number: "07796965759",
      phone_number_alternative: "",
      fax_number: "",
      web: "",
      notes: "Environmental and engineering services",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 11,
      contractor_id: "CON011",
      name: "CSC Safety",
      logo: null,
      date_review: "2020-09-18T00:00:00.000Z",
      address_1: "852 Safety Avenue",
      address_2: "",
      address_3: "",
      town: "Glasgow",
      postcode: "G1 1AA",
      email: "d.schwab@cscsafety.com",
      email_alternative: "",
      phone_number: "020 8458 7444",
      phone_number_alternative: "",
      fax_number: "",
      web: "",
      notes: "Construction safety services",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 12,
      contractor_id: "CON012",
      name: "Diamond Cleaning Services",
      logo: null,
      date_review: "2020-09-18T00:00:00.000Z",
      address_1: "963 Clean Street",
      address_2: "",
      address_3: "",
      town: "Aberdeen",
      postcode: "AB1 1AA",
      email: "info@diamondcleaning.co.uk",
      email_alternative: "",
      phone_number: "01224 123456",
      phone_number_alternative: "",
      fax_number: "",
      web: "www.diamondcleaning.co.uk",
      notes: "Professional cleaning services",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 13,
      contractor_id: "CON013",
      name: "EcoTech Solutions",
      logo: null,
      date_review: "2023-03-15T00:00:00.000Z",
      address_1: "159 Green Tech Park",
      address_2: "Innovation Hub",
      address_3: "",
      town: "Brighton",
      postcode: "BN1 1AA",
      email: "contact@ecotech-solutions.co.uk",
      email_alternative: "",
      phone_number: "01273 987654",
      phone_number_alternative: "",
      fax_number: "",
      web: "www.ecotech-solutions.co.uk",
      notes: "Sustainable technology solutions",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 14,
      contractor_id: "CON014",
      name: "FastFix Plumbing",
      logo: null,
      date_review: "2020-09-18T00:00:00.000Z",
      address_1: "753 Water Street",
      address_2: "",
      address_3: "",
      town: "Plymouth",
      postcode: "PL1 1AA",
      email: "emergency@fastfixplumbing.co.uk",
      email_alternative: "",
      phone_number: "01752 555123",
      phone_number_alternative: "",
      fax_number: "",
      web: "www.fastfixplumbing.co.uk",
      notes: "Emergency plumbing services",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 15,
      contractor_id: "CON015",
      name: "Global Security Systems",
      logo: null,
      date_review: "2023-04-20T00:00:00.000Z",
      address_1: "456 Security Boulevard",
      address_2: "Control Center",
      address_3: "",
      town: "Nottingham",
      postcode: "NG1 1AA",
      email: "security@globalsecurity.co.uk",
      email_alternative: "",
      phone_number: "0115 123456",
      phone_number_alternative: "",
      fax_number: "",
      web: "www.globalsecurity.co.uk",
      notes: "Comprehensive security solutions",
      created_at: "2024-01-15T10:00:00.000Z",
      updated_at: "2024-01-15T10:00:00.000Z",
    },
  ];
};

const getMockContractorContacts = () => {
  return [
    {
      id: 1,
      contractor_id: 1,
      name: "John Smith",
      email: "john.smith@accounts.com",
      phone: "020 1234 5678",
      position: "Account Manager",
      created_at: "2024-01-15T10:00:00.000Z",
    },
    {
      id: 2,
      contractor_id: 2,
      name: "Karl Johnson",
      email: "karl@aservices4u.co.uk",
      phone: "0203 603 1369",
      position: "Operations Director",
      created_at: "2024-01-15T10:00:00.000Z",
    },
  ];
};

const getMockContactsForCalendar = () => {
  return {
    1: "John Smith",
    2: "Karl Johnson",
  };
};

// Mock services data
const getMockServices = () => {
  return [
    { id: 1, name: "Plumbing" },
    { id: 2, name: "Electrical" },
    { id: 3, name: "Cleaning" },
    { id: 4, name: "Landscaping" },
    { id: 5, name: "Security" },
    { id: 6, name: "HVAC" },
    { id: 7, name: "Painting" },
    { id: 8, name: "Carpentry" },
    { id: 9, name: "Roofing" },
    { id: 10, name: "Pest Control" },
  ];
};

// Mock contractor services data
const getMockContractorServices = () => {
  return [
    { id: 4, name: "Landscaping" },
    { id: 6, name: "HVAC" },
  ];
};

// Document-related API methods
export const createContractorDocument = async (contractorId, documentData) => {
  try {
    // Check if documentData is FormData (for file uploads)
    const isFormData = documentData instanceof FormData;

    const config = {
      method: "POST",
    };

    if (isFormData) {
      // For FormData, don't set Content-Type header (browser will set it with boundary)
      config.body = documentData;
    } else {
      // For JSON data
      config.headers = {
        "Content-Type": "application/json",
      };
      config.body = JSON.stringify(documentData);
    }

    const response = await apiRequest(`/${contractorId}/documents`, config);
    return response;
  } catch (error) {
    console.error("Failed to create contractor document:", error);
    throw error;
  }
};

export const updateContractorDocument = async (
  contractorId,
  documentId,
  documentData
) => {
  try {
    console.log(`📤 Updating contractor document: contractorId=${contractorId}, documentId=${documentId}`);
    
    // Check if documentData is FormData (for file uploads)
    const isFormData = documentData instanceof FormData;
    console.log(`📤 Is FormData: ${isFormData}`);

    const config = {
      method: "POST",
      body: isFormData ? documentData : JSON.stringify(documentData),
    };

    // Don't set headers for FormData - apiRequest will handle it
    if (!isFormData) {
      config.headers = {
        "Content-Type": "application/json",
      };
    }

    console.log(`📤 Calling API: POST /${contractorId}/documents/${documentId}`);

    const response = await apiRequest(
      `/${contractorId}/documents/${documentId}`,
      config
    );
    
    console.log(`✅ Update response:`, response);
    return response;
  } catch (error) {
    console.error("❌ Failed to update contractor document:", error);
    throw error;
  }
};

export const downloadContractorDocument = async (contractorId, documentId) => {
  try {
    const response = await apiRequest(
      `/${contractorId}/documents/${documentId}/download`,
      {
        method: "GET",
      }
    );
    
    if (!response.success || !response.data.presignedUrl) {
      throw new Error(response.message || "Failed to get presigned URL");
    }

    // Use presigned URL directly for download (matching Laravel's direct S3 access)
    const presignedUrl = response.data.presignedUrl;
    const filename = response.data.documentName || `document-${documentId}.pdf`;

    console.log(`✅ Using presigned URL for direct S3 download: ${filename}`);

    // Create download link with presigned URL
    const a = document.createElement("a");
    a.href = presignedUrl;
    a.download = filename;
    a.target = "_blank"; // Open in new tab for direct S3 access
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    return {
      success: true,
      message: `Document downloaded: ${filename}`,
    };
  } catch (error) {
    console.error("Failed to download contractor document:", error);
    throw error;
  }
};

export const deleteContractorDocument = async (contractorId, documentId) => {
  try {
    const response = await apiRequest(
      `/${contractorId}/documents/${documentId}`,
      {
        method: "DELETE",
      }
    );
    return response;
  } catch (error) {
    console.error("Failed to delete contractor document:", error);
    throw error;
  }
};

// Get maintenance records (tickets) for a contractor
export const getContractorMaintenanceRecords = async (contractorId) => {
  try {
    const response = await apiRequest(`/${contractorId}/maintenance`);
    return response;
  } catch (error) {
    console.error("Failed to get contractor maintenance records:", error);
    throw error;
  }
};
